<template>
	<div :id="modalName">
		<b-modal :id="modalName" title="Receive Payment" scrollable hide-footer size="xl" @shown="onModalShown">
            <template v-slot:modal-title>
                <span>Receive Payment:</span>
            </template>

            <b-form @submit.stop.prevent="receivePayment">
                <b-row class="align-items-start">
                    <b-col cols="12" md="5">
                        <b-form-group
                            id="datepicker-group-date"
                            label="Date:"
                            label-for="datepicker-date"
                            label-size="sm"
                        >
                            <b-form-datepicker
                                :key="keys.datepicker"
                                id="datepicker-date"
                                v-model="payment.date"
                                class="mb-2"
                                size="sm"
                                :date-format-options="{ year: 'numeric', month: 'numeric', day: 'numeric' }"
                                locale="en"
                            >
                            </b-form-datepicker>
                        </b-form-group>

                        <b-form-group
                            id="input-group-customer"
                            label="Customer:"
                            label-for="input-customer"
                            label-size="sm"
                        >
                            <div class="options-assign">
                                <div class="autosuggest-container">
                                    <vue-autosuggest
                                        v-model="payment.customer"
                                        :suggestions="autosuggest.customer.suggestions"
                                        @click="clickHandlerCustomer"
                                        @input="fetchResultsCustomer"
                                        @selected="onSelectedCustomer"
                                        @keydown.tab="suggestFirstCustomer($event.target.value)"
                                        :get-suggestion-value="getSuggestionValueCustomer"
                                        :input-props="{id:'autosuggest__input', placeholder:''}"
                                        @keyup.enter="suggestFirstCustomer($event.target.value)"
                                        @change="customerChanged(0)"
                                    >

                                        <b-container slot-scope="{suggestion}" fluid>
                                            <b-row>
                                                <b-col class="text-right">
                                                    {{ suggestion.item.name }}
                                                </b-col>
                                            </b-row>
                                        </b-container>
                                    </vue-autosuggest>
                                </div>
                            </div>
                        </b-form-group>

                        <b-form-group
                            id="input-group-amount"
                            label="Amount:"
                            label-for="input-amount"
                            label-size="sm"
                        >
                            <b-form-input
                                id="input-amount"
                                v-model="payment.amount"
                                type="text"
                                placeholder=""
                                size="sm"
                                autocomplete="off"
                                :key="keys.amount"
                            >
                            </b-form-input>
                        </b-form-group>

                        <b-form-group
                            id="select-group-payment-type"
                            label="Payment Type:"
                            label-for="select-payment-type"
                            label-size="sm"
                        >
                            <b-form-select
                                v-model="payment.paymentType"
                                id="select-payment-type"
                                size="sm"
                                :options="lists.paymentTypes"
                            ></b-form-select>
                        </b-form-group>

                        <b-form-group
                            id="input-group-reference"
                            label="Reference #:"
                            label-for="input-reference"
                            label-size="sm"
                        >
                            <b-form-input
                                id="input-reference"
                                v-model="payment.reference"
                                type="text"
                                placeholder=""
                                size="sm"
                                autocomplete="off"
                            >
                            </b-form-input>
                        </b-form-group>

                        <b-form-group
                            id="select-group-deposit-account"
                            label="Deposit Account:"
                            label-for="select-deposit-account"
                            label-size="sm"
                        >
                            <b-form-select
                                v-model="payment.depositAccount"
                                id="select-deposit-account"
                                size="sm"
                                :options="lists.depositAccounts"
                            ></b-form-select>
                        </b-form-group>

                        <b-form-group
                            id="input-group-notes"
                            label="Notes:"
                            label-for="input-notes"
                            label-size="sm"
                        >
                            <b-form-input
                                id="input-notes"
                                v-model="payment.notes"
                                type="text"
                                placeholder=""
                                size="sm"
                                autocomplete="off"
                            >
                            </b-form-input>
                        </b-form-group>
                        <b-form-row class="button-row">
                            <b-col cols="4">
                                <b-button type="submit" variant="primary" class="btn-sm">
                                    <span v-if="submitting">
                                        <b-spinner small type="grow"></b-spinner>
                                        Accepting Payment...
                                    </span>
                                    <span v-else>Accept Payment</span>
                                </b-button>
                            </b-col>
                        </b-form-row>
                    </b-col>

                    <b-col cols="12" md="7" class="outstanding-orders">
                        <span><strong>Available To Apply: </strong>${{ available }}</span>
                        <h6 class="mt-2">Balances</h6>
                        <p class="text-muted small mb-2 receive-payment-hint">
                            This screen is for a <strong>new</strong> payment only. “This payment” is how much of the amount above you apply here.
                            Amounts already posted from earlier payments appear under “Previously applied.”
                            To view or change an <strong>existing</strong> payment (including reset applied), open that payment from the Payments list and use Edit Payment.
                        </p>
                        <div class="table-responsive">
                        <b-table-simple hover small bordered :key="keys.outstanding">
                            <b-thead>
                                <tr>
                                    <th></th>
                                    <th>Ref</th>
                                    <th>Date</th>
                                    <th>Amount</th>
                                    <th>Remaining</th>
                                    <th>Previously applied</th>
                                    <th>This payment</th>
                                </tr>
                            </b-thead>
                            <b-tbody>
                                <tr v-for="(order, index) in transactions" :key="index">
                                    <td>
                                        <b-form-checkbox :id="'checkbox-order-' + index" :name="'checkbox-order-' + index" :value="order.id"
                                            v-model="payment.applyTo" @change="check(order.id)" size="sm" />
                                    </td>
                                    <td>{{ order.reference_number }}</td>
                                    <td>{{ order.date | moment('MM-DD-YYYY') }}</td>
                                    <td>${{ Number(order.amount).toFixed(2) }}</td>
                                    <td><span v-if="order.type == 'Invoice'">${{ Number(order.remaining).toFixed(2) }}</span></td>
                                    <td>${{ sumPreviouslyApplied(order).toFixed(2) }}</td>
                                    <td>${{ Number(order.applied).toFixed(2) }}</td>
                                </tr>
                            </b-tbody>
                        </b-table-simple>
                        </div>
                    </b-col>
                </b-row>

                <!-- <b-form-row class="button-row">
                    <b-col cols=2>
                        <b-button type="submit" variant="primary" class="btn-sm">
                            <span v-if="submitting">
                                <b-spinner small type="grow"></b-spinner>
                                Accepting Payment...
                            </span>
                            <span v-else>Accept Payment</span>
                        </b-button>
                    </b-col>
                </b-form-row> -->
            </b-form>
        </b-modal>
	</div>
</template>

<script>
    import axios from 'axios'
    import { VueAutosuggest } from "vue-autosuggest"
	export default {
		props: [
            'amount',
            'customerId'
		],

		components: {
            VueAutosuggest
		},

		data() {
			return {
				modalName: 'modal-lists-customers-receive-payment',
                payment: {
                    applyTo: [],
                    amount: 0,
                    customerId: null,
                    depositAccount: '',
                    paymentMethod: '',
                    date: this.getDate()
                },
                checkedTotal: 0,
                transactions: [],
                lists: {
                    paymentTypes: [],
                    depositAccounts: []
                },

                autosuggest: {
                    customer: {
                        suggestions: [],
                        debounceMilliseconds: 500,
                        timeout: null
                    },
                    row: {
                        suggestions: [],
                        debounceMilliseconds: 500,
                        timeout: null,
                        currentIndex: '',
                    }
                },

                keys: {
                    datepicker: 1,
                    outstanding: 1,
                    amount: 1
                },

                customerReset: 0,
                customerUpdated: 0,

                submitting: 0,
			}
		},

        methods: {
            onModalShown() {
                if (this.payment.customerId) {
                    this.getTransactions()
                }
            },

            isInvoiceRow(t) {
                return t && String(t.type || '').trim() === 'Invoice'
            },

            /** Sum of amounts already posted to this invoice from existing payments (API applied_to map). */
            sumPreviouslyApplied(order) {
                const at = order && order.applied_to
                if (!at || typeof at !== 'object' || Array.isArray(at)) {
                    return 0
                }
                return Object.keys(at).reduce((sum, key) => sum + Number(at[key] || 0), 0)
            },

            check(rowId) {
                let checked = this.payment.applyTo.includes(rowId)
                let index = this.findIndex(this.transactions, 'id', rowId)
                let remaining = Number(this.transactions[index].remaining)

                if (checked) {
                    if (this.available > 0) {
                        if (remaining <= this.available) {
                            this.transactions[index].applied = remaining
                            this.checkedTotal += +remaining
                        } else {
                            this.transactions[index].applied = this.available
                            this.checkedTotal += +this.available
                        }
                    } else {
                        let index = this.payment.applyTo.indexOf(rowId)
                        this.payment.applyTo.splice(index, 1)
                        this.showAlertBox('warning', 'Balance Exceeded', 'Checked orders exceeds payment amount!')
                        this.keys.outstanding++
                    }
                } else {
                    this.checkedTotal -= +this.transactions[index].applied
                    this.transactions[index].applied = 0
                }
            },

            clickHandlerCustomer(item) {
                let query = this.payment.customer
                if (!query) {
                    this.autosuggest.customer.suggestions = []
                }
            },

            fetchResultsCustomer() {
                const query = this.payment.customer

                clearTimeout(this.autosuggest.customer.timeout);
                this.autosuggest.customer.timeout = setTimeout(() => {
                    const customersPromise = axios.get('api/lists/customers/getcustomersfilter/' + query);

                    Promise.all([customersPromise]).then(values => {
                        this.autosuggest.customer.suggestions = []

                        const customers = values[0].data.items

                        customers.length &&
                        this.autosuggest.customer.suggestions.push({ name: "customers", data: customers })

                        if (!isNaN(query)) {
                            this.payment.customer = this.autosuggest.customer.suggestions[0].data[0].name
                            this.onSelectedCustomer({item: this.autosuggest.customer.suggestions[0].data[0]})
                        }
                    })
                }, this.autosuggest.customer.debounceMilliseconds)
            },

            onSelectedCustomer(item) {
                if (item) {
                    this.customerReset = 1

                    this.payment.customer = item.item.name
                    this.payment.customerId = item.item.id
                    this.payment.paymentType = item.item.payment
                    this.payment.balance = item.item.balance
                    this.payment.open = item.item.open

                    if (item.item.last_payment) {
                        this.payment.paymentType = item.item.last_payment.payment_types_id
                        this.payment.depositAccount = item.item.last_payment.deposit_accounts_id
                    } else {
                        this.payment.paymentType = ''
                        this.payment.depositAccount = ''
                    }

                    this.customerReset = 0

                    this.getTransactions()

                    this.keys.outstanding++
                }
            },

            suggestFirstCustomer(query) {
                let suggests = this.autosuggest.customer.suggestions
                let arr = suggests[0].data

                if (arr.length) {
                    for (var i = 0; i < arr.length; i++) {
                        let cur = arr[i].name.toLowerCase()
                        if (cur.includes(query.toLowerCase())) {
                            this.customerReset = 1

                            this.payment.customer = arr[i].name
                            this.payment.customerId = arr[i].id
                            this.payment.open = arr[i].open

                            if (arr[i].last_payment) {
                                this.payment.paymentType = arr[i].last_payment.payment_types_id
                                this.payment.depositAccount = arr[i].last_payment.deposit_accounts_id
                            } else {
                                this.payment.paymentType = ''
                                this.payment.depositAccount = ''
                            }

                            this.autosuggest.customer.suggestions = []

                            this.customerReset = 0

                            this.getTransactions()

                            this.keys.outstanding++

                            break;
                        } else {
                            continue
                        }
                    }
                }
            },

            customerChanged(action) {
                if (this.customerReset == 0) {
                    this.customerUpdated = action
                }
            },

            getSuggestionValueCustomer(suggestion) {
                return suggestion.item.name
            },

            getTransactions() {
                if (!this.payment.customerId) {
                    this.transactions = []
                    return
                }
                const url = 'api/lists/customers/gettransactions/' + this.payment.customerId
                axios.get(url, {})
                    .then((res) => {
                        const payload = res.data || {}
                        const raw = Array.isArray(payload.transactions)
                            ? payload.transactions
                            : (payload.data && Array.isArray(payload.data.transactions) ? payload.data.transactions : [])
                        const rows = raw
                            .filter((i) => this.isInvoiceRow(i))
                            .map((i) => {
                                const row = Object.assign({}, i)
                                if (row.applied === undefined) {
                                    row.applied = 0
                                }
                                return row
                            })
                        rows.sort((a, b) => {
                            const ra = Number(a.reference_number)
                            const rb = Number(b.reference_number)
                            if (!isNaN(ra) && !isNaN(rb) && ra !== rb) {
                                return rb - ra
                            }
                            return String(b.reference_number || '').localeCompare(String(a.reference_number || ''), undefined, { numeric: true })
                        })
                        this.transactions = rows
                        this.keys.outstanding++
                    }, () => {
                        this.has_error = true
                    })
            },

            receivePayment() {
                this.submitting = 1
                let formData = this.payment

                let url = 'api/lists/customers/payments/receivepayment'
                axios.post(url, {
                    formData,
                    transactions: this.transactions
                })
                .then((res) => {
                    this.submitting = 0

                    if (res.data.status == 'Success') {
                        this.closeModal(this.modalName)
                        this.showAlertBox('success', 'Payment Received', 'Payment received successfully!')
                        this.$emit('getPayments')
                    } else {
                        this.showAlertBox('error', 'Error', 'Error receiving payment!')
                    }
                }, () => {
                    this.has_error = true
                })
            },

            getPaymentTypes() {
                let url = 'api/lists/customers/payments/getpaymenttypes'
                axios.get(url, {})
                .then((res) => {
                    this.lists.paymentTypes = res.data
                }, () => {
                    this.has_error = true
                })
            },

            getPaymentDepositAccounts() {
                let url = 'api/company/payment-deposit-accounts/active'
                axios.get(url, {})
                .then((res) => {
                    if (res.data.status === 'success') {
                        // Transform the data to match the expected format
                        this.lists.depositAccounts = res.data.deposit_accounts.map(account => ({
                            value: account.id,
                            text: account.name
                        }))
                    }
                }, (error) => {
                    this.has_error = true
                })
            },
		},

		computed: {
            available() {
                let avail = this.payment.amount - this.checkedTotal
                return avail.toFixed(2)
            }
		},

		mounted() {
            this.getPaymentTypes()
            this.getPaymentDepositAccounts()

            let now = this.$moment()
            this.payment.date = now.format('YYYY-MM-DD')
            this.keys.datepicker++

            if (this.amount) {
                this.payment.amount = this.amount
            }

            if (this.customerId) {
                this.payment.customer = this.customerId
                this.fetchResultsCustomer()
            }
		}
	}
</script>
<style lang="scss">
    #modal-lists-customers-receive-payment {
        .modal-content {
            .button-row {
                margin-top: 15px;
            }
        }

        .outstanding-orders {
            border-left: 1px #ccc solid;

            h6 {
                text-align: center;
            }

            table {
                font-size: 10pt;
            }
        }

        .options-assign {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif;

            input {
                width: 100%;
                padding: 0.5rem;
                height: calc(1.5em + 0.3rem + 2px);
            }

            ul {
                width: 100%;
                color: rgba(30, 39, 46,1.0);
                list-style: none;
                margin: 0;
                padding: 0.5rem 0 .5rem 0;
            }

            li {
                margin: 0 0 0 0;
                border-radius: 5px;
                padding: 0.75rem 0 0.75rem 0.75rem;
                display: flex;
                align-items: center;
            }

            li:hover {
                cursor: pointer;
            }

            .autosuggest-container {
                display: flex;
                width: 100%;

                li {
                    white-space: nowrap;
                    text-align: left;
                }
            }

            #autosuggest { width: 100%; display: block; }

            .autosuggest__results {
                position: relative;
            }

            .autosuggest__results-container {
                width: 300px;
            }

            .autosuggest__results-item--highlighted {
                background-color: rgba(51, 217, 178,0.2);
            }

            #autosuggest-autosuggest__results {
                overflow-y: hidden;
            }
        }
    }
</style>
